"use client";

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export type TabItem = { id: string; label: ReactNode; content: ReactNode; hint?: ReactNode };

/**
 * Accessible tabs (roving tabindex, arrow keys). `orientation="vertical"` puts
 * the tab list in a left column on desktop, the pattern used for module indexes.
 */
export function Tabs({
  items,
  orientation = "horizontal",
  className,
  listClassName,
  panelClassName,
  label,
  onDark = false,
}: {
  items: TabItem[];
  orientation?: "horizontal" | "vertical";
  className?: string;
  listClassName?: string;
  panelClassName?: string;
  label: string;
  onDark?: boolean;
}) {
  const [active, setActive] = useState(0);
  const base = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const vertical = orientation === "vertical";

  const onKey = (e: KeyboardEvent) => {
    const next = vertical ? ["ArrowDown", "ArrowRight"] : ["ArrowRight", "ArrowDown"];
    const prev = vertical ? ["ArrowUp", "ArrowLeft"] : ["ArrowLeft", "ArrowUp"];
    let i = active;
    if (next.includes(e.key)) i = (active + 1) % items.length;
    else if (prev.includes(e.key)) i = (active - 1 + items.length) % items.length;
    else if (e.key === "Home") i = 0;
    else if (e.key === "End") i = items.length - 1;
    else return;
    e.preventDefault();
    setActive(i);
    refs.current[i]?.focus();
  };

  return (
    <div className={cn(vertical ? "grid gap-8 lg:grid-cols-[minmax(15rem,4fr)_8fr] lg:gap-12" : "", className)}>
      <div
        role="tablist"
        aria-label={label}
        aria-orientation={orientation}
        onKeyDown={onKey}
        className={cn(
          vertical
            ? "flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible"
            : cn("flex gap-6 overflow-x-auto border-b", onDark ? "border-line-dark" : "border-line"),
          listClassName,
        )}
      >
        {items.map((t, i) => {
          const selected = i === active;
          return (
            <button
              key={t.id}
              ref={(el) => {
                refs.current[i] = el;
              }}
              role="tab"
              id={`${base}-t-${t.id}`}
              aria-selected={selected}
              aria-controls={`${base}-p-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(i)}
              className={cn(
                "shrink-0 text-left font-semibold transition-colors",
                vertical
                  ? cn(
                      "rounded-panel border-l-2 px-4 py-3 lg:w-full",
                      selected
                        ? onDark
                          ? "border-tan bg-navy-2 text-white"
                          : "border-navy bg-fog text-navy"
                        : onDark
                          ? "border-transparent text-mist hover:text-white"
                          : "border-transparent text-slate hover:bg-fog-2 hover:text-navy",
                    )
                  : cn(
                      "-mb-px border-b-2 pb-3 text-[0.9375rem]",
                      selected
                        ? onDark
                          ? "border-white text-white"
                          : "border-navy text-navy"
                        : onDark
                          ? "border-transparent text-mist hover:text-white"
                          : "border-transparent text-slate hover:text-navy",
                    ),
              )}
            >
              <span className="block">{t.label}</span>
              {vertical && t.hint && (
                <span className={cn("mt-0.5 hidden text-small font-normal lg:block", onDark ? "text-mist" : "text-slate")}>
                  {t.hint}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {items.map((t, i) => (
        <div
          key={t.id}
          role="tabpanel"
          id={`${base}-p-${t.id}`}
          aria-labelledby={`${base}-t-${t.id}`}
          hidden={i !== active}
          tabIndex={0}
          className={cn(!vertical && "pt-8", panelClassName)}
        >
          {t.content}
        </div>
      ))}
    </div>
  );
}
